import { isNil } from 'lodash';
import React from 'react';
import { styled } from '~ui-kit/styled-components';
import {
  styledColor,
  StyledColorProps,
  styledHeight,
  StyledHeightProps,
  StyledSizeProps,
  styledSpace,
  StyledSpaceProps,
  styledWidth,
  StyledWidthProps,
} from '~ui-kit/theme/styled-system';
import { Box } from '../box';

type IconStyleProps = Pick<StyledSpaceProps, '$m' | '$mb' | '$ml' | '$mr' | '$mt' | '$mx' | '$my'> &
  StyledSizeProps &
  StyledWidthProps &
  StyledHeightProps &
  StyledColorProps;

export interface IconProps extends IconStyleProps {
  /** Icon component that will be wrapped by the Icon container */
  children: React.ReactNode;
}

export type IconComponentProps = Omit<IconProps, 'children'>;

/**
 * Icon component that wraps an SVG icon and provides a consistent styling
 * and size management.
 *
 * Defining an icon:
 *
 * Wrap the svg with this component into a new component, ex: "icons/my-new-icon.tsx"
 * ```tsx
 * import { Icon, IconComponentProps } from "../icon/icon";
 * export function MyNewIcon(props: IconComponentProps) {
 *   return (
 *    <Icon {...props}>
 *      <svg>...</svg>
 *    </Icon>
 *   );
 *  }
 * ```
 *
 * Importing:
 * ```tsx
 * import { MyNewIcon } from "~ui-kit/components/icons/my-new-icon";
 * // ...
 * <MyNewIcon $m="10px" $color="red" />
 * // ...
 * ```
 */
export function Icon({ children, $size, $width, $height, ...props }: IconProps) {
  let width = $width;
  let height = $height;

  // $size is just a shortcut to overrides both width and height
  if (!isNil($size)) {
    width = $size;
    height = $size;
  }

  // Default size when none specified
  if (isNil(width) && isNil(height)) {
    width = height = '24px';
  }

  return (
    <IconContainer $width={width} $height={height} {...props}>
      {children}
    </IconContainer>
  );
}

const IconContainer = styled(Box)<IconStyleProps>`
  display: inline-flex;
  vertical-align: middle;
  ${styledSpace}
  ${styledWidth}
  ${styledHeight}
  
  svg {
    max-width: 100%;
    max-height: 100%;
    ${styledColor}
  }
`;
